import React from 'react';
import { Grid, Tab } from 'semantic-ui-react';
import { DragDropContext } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import { change, initialize } from 'redux-form';

import { Filter, Main } from 'Containers/';
import { createToast, toastType } from 'Common/ToastHelper';
import * as DNDHelpers from 'Pages/productionreportsetup/helpers/dnd';
import { useApi } from 'Common/Hooks';
import { HTTP_METHODS } from 'Common/Constants';
import { LoadingBase } from 'Components/';
import {
  REPORT_WRITER_TABS,
  REPORT_SECTION_TABS,
  REPORT_OPTIONS_SECTION_TITLE,
  ValuesByTemplate,
  Templates,
} from 'Pages/productionreportsetup/helpers/Constants';
import { PRODUCTION_REPORT_SETUP_CREATE_REPORT_TEMPLATE } from 'Common/FormEnum';
import { ProductionReportSetupFields } from 'Common/FieldEnum';
import { getIsDirty } from 'Common/Selectors/DirtySelector';

import 'Styles/pages/productionreportsetup/createproductionreporttemplate.less';

import { ReportOptions } from './components/ReportOptions';
import { SelectedReportOptions } from './components/SelectedReportOptions';
import { CreateProductionReportTemplateForm } from './components/CreateProductionReportTemplateForm';

function CreateProductionReportTemplate({
  match,
  change,
  initialize,
  history,
}) {
  const {
    params: { id },
  } = match;
  const [tabIndex, setTabIndex] = React.useState(0);
  const [headerOptions, setHeaderOptions] = React.useState(
    ValuesByTemplate[Templates.Simple].Options
  );
  const [reportColumns, setReportColumns] = React.useState(
    ValuesByTemplate[Templates.Simple].InitialSection
  );

  const [
    { loading: loadingReportSetup, data: reportSetupResponse },
    { request: reportSetupRequest },
  ] = useApi({}, { method: HTTP_METHODS.GET });
  const [
    { loading: saveLoading, success: saveSuccess },
    { request: saveRequest },
  ] = useApi(
    {},
    { showSuccessToast: true, successToastMessage: 'Saved Successfully.' }
  );

  const mainProps = React.useMemo(
    () =>
      ({
        toolbox: {
          className: 'cprt-main-toolbox',
          formName: PRODUCTION_REPORT_SETUP_CREATE_REPORT_TEMPLATE,
          saveButtonProps: {
            content: 'Save & Close',
          },
          rightButtons: [
            {
              primary: false,
              content: 'Cancel',
              className: 'basic white',
              onClick: () => history.push('/productionreportsetup'),
            },
            {
              primary: false,
              content: 'Preview',
              className: 'basic white',
            },
          ],
          shown: true,
        },
      }),
    []
  );

  React.useEffect(() => {
    reportSetupRequest(`Production/GetProductionReportSetup/${id}`, {});
  }, []);

  React.useEffect(() => {
    if (reportSetupResponse && reportSetupResponse.Data) {
      const reportSetup = reportSetupResponse.Data;
      const values = ValuesByTemplate[reportSetup.TemplateId];

      if (!reportSetup.Settings) {
        reportSetup.Settings = values.InitialSection;
        setReportColumns(values.InitialSection);
        setHeaderOptions(
          DNDHelpers.getUnusedOptions(values.Options, values.InitialSection)
        );
      } else {
        const reportColumns = JSON.parse(reportSetup.Settings);
        reportSetup.Settings = reportColumns;
        setReportColumns(reportColumns);
        setHeaderOptions(
          DNDHelpers.getUnusedOptions(values.Options, reportColumns)
        );
      }
      initialize(PRODUCTION_REPORT_SETUP_CREATE_REPORT_TEMPLATE, reportSetup);
    }
  }, [reportSetupResponse]);

  React.useEffect(() => {
    if (saveSuccess) {
      history.push('/productionreportsetup');
    }
  }, [saveSuccess]);

  const onDeleteColumn = (index) => {
    const reportSection = REPORT_SECTION_TABS[tabIndex];
    if (reportColumns[reportSection][index].length > 0) {
      createToast(
        toastType.error,
        'Error',
        'Column contains data, unable to remove.',
        true
      );
      return;
    }

    const updatedReportColumns = { ...reportColumns };
    const updatedSection = [...reportColumns[reportSection]];
    updatedSection.splice(index, 1);
    updatedReportColumns[reportSection] = updatedSection;
    console.log(updatedReportColumns);

    setReportColumns(updatedReportColumns);
  };

  const panes = React.useMemo(
    () => [
      {
        menuItem: 'Report Header',
        render: () => (
          <SelectedReportOptions
            reportColumns={reportColumns}
            setReportColumns={setReportColumns}
            selectedColumn={REPORT_WRITER_TABS.ReportHeader}
            onDeleteColumn={onDeleteColumn}
          />
        ),
      },
      {
        menuItem: 'Planned',
        render: () => (
          <SelectedReportOptions
            reportColumns={reportColumns}
            setReportColumns={setReportColumns}
            selectedColumn={REPORT_WRITER_TABS.Planned}
            onDeleteColumn={onDeleteColumn}
          />
        ),
      },
      {
        menuItem: 'Offered',
        render: () => (
          <SelectedReportOptions
            reportColumns={reportColumns}
            setReportColumns={setReportColumns}
            selectedColumn={REPORT_WRITER_TABS.Offered}
            onDeleteColumn={onDeleteColumn}
          />
        ),
      },
      {
        menuItem: 'Served',
        render: () => (
          <SelectedReportOptions
            reportColumns={reportColumns}
            setReportColumns={setReportColumns}
            selectedColumn={REPORT_WRITER_TABS.Served}
            onDeleteColumn={onDeleteColumn}
          />
        ),
      },
    ],
    [reportColumns]
  );

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const reportSection = REPORT_SECTION_TABS[tabIndex];

    const {
      updatedHeaderOptions,
      updatedReportColumns,
      error,
    } = DNDHelpers.moveOption({
      headerOptions,
      reportSection,
      reportColumns,
      source,
      destination,
      draggableId,
    });

    if (error) {
      createToast(toastType.error, 'Error', error, true);
      return;
    }

    if (updatedHeaderOptions) {
      setHeaderOptions(updatedHeaderOptions);
    }

    if (updatedReportColumns) {
      setReportColumns(updatedReportColumns);
      change(
        PRODUCTION_REPORT_SETUP_CREATE_REPORT_TEMPLATE,
        ProductionReportSetupFields.Settings,
        updatedReportColumns
      );
    }
  };

  const onTabChange = (e, data) => {
    const index = data.activeIndex;
    setTabIndex(index);
  };

  const onSubmit = (formValues) => {
    let hasEmptyColumn = false;

    // Check if there are any empty columns
    Object.keys(formValues.Settings).forEach((sectionName) => {
      const reportSection = formValues.Settings[sectionName];

      const emptyColumns = reportSection.filter(
        (column) => column.length === 0
      );

      if (emptyColumns.length > 0) {
        hasEmptyColumn = true;
      }
    });

    if (hasEmptyColumn) {
      createToast(
        toastType.error,
        'Error',
        'Report contains an empty column, unable to save',
        true
      );
      return;
    }

    const updatedFormValues = { ...formValues };
    updatedFormValues[ProductionReportSetupFields.Settings] = JSON.stringify(
      reportColumns
    );

    updatedFormValues.ReportNotes = updatedFormValues.Description;

    saveRequest.post('Production/SaveProductionReportSetup', updatedFormValues);
  };

  if (loadingReportSetup || saveLoading) {
    return <LoadingBase />;
  }
  return (
    <React.Fragment>
      <Filter title="Production Report Setup" />
      <Main className="cprt-main" shouldNotUsePageWidth {...mainProps}>
        <CreateProductionReportTemplateForm onSubmit={onSubmit}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Grid className="cprt-grid-container">
              <Grid.Column width={12}>
                <Tab
                  panes={panes}
                  activeIndex={tabIndex}
                  onTabChange={onTabChange}
                />
              </Grid.Column>
              <Grid.Column className="cprt-header-options" width={4}>
                <ReportOptions
                  title={REPORT_OPTIONS_SECTION_TITLE[tabIndex]}
                  options={headerOptions[REPORT_SECTION_TABS[tabIndex]]}
                />
              </Grid.Column>
            </Grid>
          </DragDropContext>
        </CreateProductionReportTemplateForm>
      </Main>
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  ownProps.setPageLevelDirty(
    getIsDirty(PRODUCTION_REPORT_SETUP_CREATE_REPORT_TEMPLATE)(state)
  );

  return {};
};

const mapDispatchToProps = {
  change,
  initialize,
};

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateProductionReportTemplate);

ConnectedComponent.path = '/productionreportsetup/:id';

export { ConnectedComponent as CreateProductionReportTemplate };



// WEBPACK FOOTER //
// ./src/pages/productionreportsetup/subpages/createreporttemplate/CreateProductionReportTemplate.js